import { useHeaderStore } from "state/headerState/header.store";
import { Capacitor } from "@capacitor/core";
import { ColoursObj, TextInputField, Typography } from "@eventr/common-storybook";
import { useNativeNavigationViewContext } from "@eventr/native/navigation/react";
import { Box, Stack } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { HeaderSearchBar } from "../../home/components/carousel/slide/footer/HeaderSearchBar";
import i18n from "../../i18n/i18next";
import { useRocketchatUiStore } from "../../state/rocketchatState/rocketchatUi.store";
import { useScrollDisabled } from "../../state/scrollState/scrollDisabledContext";
import { useScrollContext } from "../../state/scrollState/scrollContext";
import { useKeyboardOpen } from "../../state/windowState/window.store";
import { NativeToolbar } from "../nativeNavigation/NativeToolbar";
import { isIosNativeNavigation } from "../nativeNavigation/platform";
import { nativeTabbarReady, nativeTabbarVisible, useTabBarBottomSafeOffset } from "./tab-bar/nativeTabbarBridge";
import { useNavBarHidden } from "./tab-bar/navBar.store";
import { usePaneNavigationBar, useTrailingPaneUnsaved } from "./panes/pane-navigation";
import { ToolbarItem, ToolbarItemProps } from "./TopToolbar";

interface NavigationViewProps {
  // The contents of the NavigationView.
  children: React.ReactNode;

  // The title of the navigation view, displayed in the navigation bar with
  // bold styling.
  navigationBarTitle?: string | React.ReactNode;

  // A short identifier for what this page acts on (e.g. an itinerary title) — becomes
  // UINavigationItem.prompt under iOS native navigation, one truncating line, so keep it brief.
  navigationBarSubtitle?: string | React.ReactNode;

  // A rasterised (PNG data URI) avatar/icon shown to the left of the title in the
  // native nav bar, for titles richer than plain text (e.g. a chat's avatar + name).
  // Web is unaffected: it renders whatever `navigationBarTitle` is (e.g. <ChatTitle
  // />); this only feeds the native titleView.
  navigationBarTitleImage?: string;

  // The plain-text title to use natively alongside navigationBarTitleImage. Needed
  // because navigationBarTitle is often a custom component (e.g. <ChatTitle />) with
  // no text this component can extract from its props, so the caller passes the
  // resolved string directly (it computed navigationBarTitleImage from the same
  // data). Ignored unless navigationBarTitleImage is also set.
  navigationBarTitleImageText?: string;

  // Called when the native title view is tapped. Has no effect unless
  // navigationBarTitleImage is also set (a plain text title has no native tap
  // target). Ignored on web, where the title element (e.g. <ChatTitle />) handles
  // its own click.
  navigationBarTitleAction?: () => void;

  // Inline shows the title beside the toolbar items; large omits it from the web header so
  // the page renders its own heading. No effect under iOS native nav, which owns the title either way.
  navigationBarDisplayMode?: "inline" | "large";

  // If nil, no back button is displayed. If not nil, then a button is
  // displayed as the first item in the leading navigation bar, styled with a
  // chevron pointing to the left. When pressed, this changes the page
  // transitions to use transitions specific to going back in the navigation
  // hierarchy. As a result, actions should always exit the current
  // navigation view.
  backButtonAction?: () => void;

  // The items to render in the leading navigation bar. Note that if a back
  // button action is provided, then the back button will be the first leading
  // item.
  navigationBarLeading?: ToolbarItemProps[];

  // On a pushed page, keep the system back chevron and its swipe-to-pop gesture
  // alongside navigationBarLeading instead of the leading items replacing them.
  supplementBackButton?: boolean;

  // Whether backButtonAction (tap or swipe) might actually refuse to leave. Leave
  // undefined for almost every screen, which defaults to false (native pops directly,
  // swipe-back works, backButtonAction is never asked). Pass true where backButtonAction
  // may do something other than navigate away, e.g. a discard-unsaved-changes dialog. See
  // NativeToolbar's own doc comment.
  backMayRefuse?: boolean;
  /** Discards the screen's unsaved changes when the user leaves them from the other pane. */
  onDiscardUnsaved?: () => unknown;

  // The items to render in the trailing navigation bar.
  navigationBarTrailing?: ToolbarItemProps[];

  // Whether to animate entry and exit using framer-motion, with support for
  // changing transitions based on whether the view is going up or down the
  // navigation hierarchy.
  usePageTransition?: boolean;

  // Unused property. Designed to present buttons in a floating container.
  footer?: React.ReactNode;

  /// I dont have time to test all pages so only changing the page that i care about.
  /// Needs to be sorted properly...
  removeTopPadding?: boolean;

  //back button custom react node
  backButton?: ToolbarItemProps;

  // Native-only override for the leading bar button. A sheet root already gets a
  // derived "Close" calling backButtonAction (see NativeToolbar), so set this only to
  // replace that, e.g. with "Cancel". Pushed pages get the native chevron for free.
  nativeLeadingButton?: ToolbarItemProps;

  // Whether the enclosing sheet can be swipe-dismissed. Applies to the whole sheet, not just this view. iOS only.
  cancellable?: boolean;

  // Called when a swipe-down dismiss was refused (cancellable false) while this view was
  // topmost. Should end in dismiss(), not the pop backButtonAction uses. iOS only.
  onDismissAttempt?: () => void;

  /// Added for booking hotel page image under header
  pictureMode?: boolean;

  profilePage?: boolean;

  count?: number;

  groupNameField?: boolean;

  userSearchBar?: boolean;

  stickyUnderlay?: React.ReactNode;

  autoHeaderPadding?: boolean;

  // Skips the fixed 70px spacer this view normally reserves below its content for a tab
  // bar it doesn't have (e.g. a full-screen pushed picker with no footer/tab bar at all).
  hideFooterSpacer?: boolean;

  // Switches off the shared MobileAppFrame scroll container for as long as this view is
  // mounted, for a view that renders its own inner scroller (e.g. a full-bleed calendar or
  // clock) and would otherwise fight the app-wide scroll container for the same touch/scroll
  // gesture. See useScrollDisabled's doc comment.
  disablesFrameScroll?: boolean;
}

/** Space a bottom-pinned footer must leave for the tab bar or home indicator; none while the keyboard is up. */
export const useFooterDeadZone = () => {
  const keyboardOpen = useKeyboardOpen();
  const tabBarOffset = useTabBarBottomSafeOffset();

  if (Capacitor.isNativePlatform() && keyboardOpen) {
    // capacitor.config.ts sets Keyboard.resize = KeyboardResize.Native on both platforms, so the
    // webview's own viewport already shrinks by the keyboard height. Adding keyboardHeight again
    // here would double-shift the footer above the keyboard.
    return "0px";
  }
  // iOS native navigation without a native tab bar (e.g. the auth flow): there is no
  // tab-bar offset and the web nav bar is not rendered, so getTabBarBottomSafeOffset
  // returns 0 and a fixed footer would sit under the home indicator. Clear it with the
  // bottom safe-area inset, the same pattern used for the top inset on the scroll
  // container in routes.tsx (--native-navigation-inset-bottom where the plugin injects
  // it, env() as the fallback on stock iOS).
  if (isIosNativeNavigation() && !nativeTabbarVisible) {
    return "var(--native-navigation-inset-bottom, env(safe-area-inset-bottom, 0px))";
  }
  return tabBarOffset + "px";
};

export const NavigationView = ({
  children,
  profilePage,
  navigationBarTitle,
  navigationBarSubtitle,
  navigationBarTitleImage,
  navigationBarTitleImageText,
  navigationBarTitleAction,
  navigationBarDisplayMode,
  backButtonAction,
  navigationBarLeading,
  supplementBackButton,
  backMayRefuse,
  onDiscardUnsaved,
  navigationBarTrailing: trailingItems,
  usePageTransition,
  footer,
  removeTopPadding = false,
  backButton,
  nativeLeadingButton,
  cancellable,
  onDismissAttempt,
  pictureMode = false,
  count = 0,
  userSearchBar = false,
  groupNameField = false,
  stickyUnderlay,
  autoHeaderPadding = false,
  hideFooterSpacer = false,
  disablesFrameScroll = false,
}: NavigationViewProps) => {
  const paneBar = usePaneNavigationBar();
  // A screen whose Back may refuse has unsaved changes.
  useTrailingPaneUnsaved(backMayRefuse ?? false, onDiscardUnsaved);
  const navigationBarTrailing = paneBar.closeItem ? [...(trailingItems ?? []), paneBar.closeItem] : trailingItems;
  const footerBottom = useFooterDeadZone();

  const { setDisabled: setFrameScrollDisabled } = useScrollDisabled();
  useEffect(() => {
    if (!disablesFrameScroll) return;
    setFrameScrollDisabled(true);
    return () => setFrameScrollDisabled(false);
  }, [disablesFrameScroll, setFrameScrollDisabled]);

  // Holds the scroll offset, which is used to present and hide the shadow on
  // the navigation bar. Also holds the useBackAnimation property, which is
  // toggled to true when a back button is pressed during the duration of the
  // animation. This ensures that other navigation views that are returned to
  // accordingly use the correct entry animation.
  const [atTop, setAtTop] = useState(useScrollContext.getState().atTop);

  const viewHeaderRef = useRef<HTMLDivElement>(null);
  // Connect to the store on mount, disconnect on unmount, catch state-changes in a reference
  useEffect(() => useScrollContext.subscribe((state) => setAtTop(state.atTop)), [setAtTop]);

  const [groupName, setGroupName] = useState<string>();

  useEffect(() => {
    if (userSearchBar) {
      useRocketchatUiStore.getState().setGroupChatName(groupName);
    }
  }, [userSearchBar, groupName]);

  // The member picker's search writes the app-wide header query, which the chat list
  // also filters by: leaving Manage/New Group Chat left the list filtered to whatever was
  // typed there ("No Results"). Clear it when this view goes.
  useEffect(() => {
    if (!userSearchBar) return;
    return () => useHeaderStore.getState().setQuery("");
  }, [userSearchBar]);

  const navBarHidden = useNavBarHidden();
  const footerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  // The footer is fixed, so the body reserves its height below the content (plus 70px for
  // the web tab bar, where one is rendered). Measured through a ResizeObserver rather than
  // once per mount: a footer whose buttons depend on loaded or toggled state changes height
  // after the first pass, and a stale measurement leaves the end of the content stranded
  // underneath it. Falls back to the single measurement where ResizeObserver is absent (jsdom).
  useEffect(() => {
    const tabBarSpace = hideFooterSpacer || navBarHidden || nativeTabbarReady ? 0 : 70;
    const node = footerRef.current;
    const measure = () => setHeight((node?.clientHeight ?? 0) + tabBarSpace);

    measure();

    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [navBarHidden, hideFooterSpacer]);

  const getPageTitle = (navBatTitle: React.ReactNode) => {
    // React 19 narrows isValidElement to ReactElement<unknown> rather than <any>, so the
    // expected props shape has to be named for `.props.children` to be readable.
    if (React.isValidElement<{ children?: React.ReactNode }>(navBatTitle)) {
      const children = React.Children.toArray(navBatTitle.props.children);
      const firstChild = children[0];
      return typeof firstChild === "string" ? firstChild : "";
    }
  };

  // Native bar title/subtitle only understand plain strings; a rich ReactNode title
  // (rare) has no native equivalent and is left as-is on the web header.
  const toNativeText = (node: React.ReactNode): string | undefined => {
    if (typeof node === "string") return node;
    if (React.isValidElement(node)) return getPageTitle(node) || undefined;
    return undefined;
  };

  // Keeps the native nav bar's title/prompt in sync with the web props. Runs in a
  // layout effect (via useNativeNavigationViewContext) before paint, and only calls
  // into the plugin when the resolved text actually changed, so a page whose
  // title/subtitle is already known synchronously never flashes, and one that
  // resolves async (e.g. after a fetch) updates natively the instant React re-renders
  // with the real value instead of a placeholder.
  //
  // title is omitted (not cleared to null) when this prop isn't given: some pages
  // render NavigationView without a title and rely on the registry-declared title set
  // at view creation (NativeNavigationViewSetup) instead; sending null here would wipe
  // that out before this page's own title (if any) is known. subtitle has no such
  // fallback, so it always syncs, including clearing back to null when a page toggles
  // it off (e.g. `isNew ? undefined : itineraryTitle`).
  //
  // navigationBarTitleImage switches the native side to a titleView (image + labels)
  // instead of the plain title/subtitle pair, for richer titles (e.g. a chat's avatar
  // + name) that a plain string can't represent. The web rendering of
  // navigationBarTitle (e.g. <ChatTitle />) is untouched either way.
  const nativeTitle = toNativeText(navigationBarTitle);
  const nativeSubtitle = toNativeText(navigationBarSubtitle);
  const nativeNavigationViewContext = useNativeNavigationViewContext(
    isIosNativeNavigation()
      ? navigationBarTitleImage
        ? {
            titleView: {
              // scale: 3 matches the rasterisation convention used everywhere else in
              // the app (rasterizeAvatar/rasterizeReactIcon, TOOLBAR_ICON_SCALE). The
              // canvas is drawn at 3x point size, so this must match or native has no
              // way to know the physical-pixel image is 3x oversized and displays it
              // at the wrong (much larger) point size.
              image: { uri: navigationBarTitleImage, scale: 3 },
              title: navigationBarTitleImageText ?? nativeTitle ?? null,
              subtitle: nativeSubtitle ?? null,
              tappable: !!navigationBarTitleAction,
            },
          }
        : {
            ...(nativeTitle !== undefined ? { title: nativeTitle } : {}),
            subtitle: nativeSubtitle ?? null,
            titleView: null,
          }
      : undefined,
  );

  // Bridges a tap on the native titleView back to navigationBarTitleAction. Read
  // through a ref so the listener (registered once) always calls the latest action.
  const navigationBarTitleActionRef = useRef(navigationBarTitleAction);
  navigationBarTitleActionRef.current = navigationBarTitleAction;
  useEffect(() => {
    if (!isIosNativeNavigation()) return;
    return nativeNavigationViewContext.addClickListener(({ buttonId }) => {
      if (buttonId === "titleView") navigationBarTitleActionRef.current?.();
    });
  }, [nativeNavigationViewContext]);

  const titleLength = typeof navigationBarTitle === "string" ? navigationBarTitle.length : 0;

  // The title typography.
  const titleTypography = navigationBarTitle && (
    <Typography
      variant={titleLength > 20 ? "bodyBold" : "mobileH3"}
      id={titleLength === 0 ? String(getPageTitle(navigationBarTitle)) : String(navigationBarTitle)}
      style={{
        lineClamp: 2,
        boxOrient: "vertical",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2,
      }}
      sx={{
        textAlign: "center",
        margin: "auto",
        width: "100%",
        display: "-webkit-box",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {navigationBarTitle}
    </Typography>
  );

  // The subtitle typography.
  const subtitleTypography = navigationBarSubtitle && (
    <Typography
      id={String(navigationBarSubtitle)}
      variant="bodySubtext"
      color={ColoursObj.core.primary}
      sx={{
        textAlign: "center",
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {navigationBarSubtitle}
    </Typography>
  );

  // The back button. Carries out the back button action if provided, toggled
  // use back animation to true before.
  const backButtonDefault: ToolbarItemProps = {
    id: "backButtonNavigation",
    icon: "Chevron_Left",
    action: () => {
      if (backButtonAction) {
        backButtonAction();
      }
    },
  };

  const topPadding = () => {
    // On native, the native navigation bar replaces this web header, so no space
    // needs to be reserved for it.
    if (isIosNativeNavigation()) return "0px";
    if (pictureMode) return "0px";
    if (removeTopPadding) return "";
    if (autoHeaderPadding) return viewHeaderRef.current?.offsetHeight + "px";
    return "64px";
  };
  // The group-name input (chat creation) and user search bar (chat member picker) are
  // rendered here in `body`, not in `navigationBar` (which is null under iOS native
  // nav, see below). They aren't really nav-bar chrome, and `body` is always present
  // regardless of platform.
  const belowBarContent = (groupNameField || userSearchBar) && (
    <Stack
      sx={{
        paddingX: "16px",
      }}
    >
      {groupNameField && (
        <TextInputField
          value={groupName}
          type="text"
          fieldLabel={i18n.t("rocketChat.groupName")}
          autoComplete="off"
          onChange={(event) => {
            setGroupName(event.currentTarget.value);
          }}
        />
      )}
      {userSearchBar && <HeaderSearchBar />}
    </Stack>
  );

  const body = (
    <Stack
      sx={{
        height: "100%",
        width: "var(--app-width)",
        bgcolor: ColoursObj.backgrounds.primary,
      }}
    >
      <Stack
        sx={{
          paddingTop: topPadding,
        }}
      >
        {belowBarContent}

        {/* Under iOS native navigation, stickyUnderlay (e.g., SearchBar) must be rendered
            here in the body since the web navigationBar is not rendered (set to null). */}
        {isIosNativeNavigation() && stickyUnderlay && (
          <Stack
            sx={{
              padding: "0 16px 8px",
            }}
          >
            {stickyUnderlay}
          </Stack>
        )}

        {children}

        <div style={{ height: height }} />
      </Stack>
    </Stack>
  );

  // The navigation bar, containing leading and trailing toolbar items and the
  // title and subtitle typography. Presents with a shadow if the scroll
  // offset is none zero.
  // On native, the native navigation bar shows the title and back button, and the
  // trailing items are set as native bar buttons by <NativeToolbar> (rendered below),
  // so the web header renders nothing here.
  const navigationBar = isIosNativeNavigation() ? null : (
    <Stack
      id="NavigationViewHeader"
      ref={viewHeaderRef}
      sx={{
        paddingX: "16px",

        bgcolor: pictureMode ? "transparent" : ColoursObj.backgrounds.primary,

        paddingBottom: "8px",
      }}
    >
      <Stack
        direction="row"
        sx={{
          height: removeTopPadding ? "" : "56px",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack
          direction="row"
          sx={{
            gap: "8px",
            minWidth: "70px",
            justifyContent: "start",
          }}
        >
          {/* Added so that back button wont appear if not given a function. Also should not do anything if not given a function. */}
          <Box>{backButtonAction && !paneBar.hidesBack && <ToolbarItem props={backButton ?? backButtonDefault} />}</Box>

          {navigationBarLeading &&
            navigationBarLeading.map((toolbarItem, index) => {
              return (
                <div key={index}>
                  <ToolbarItem props={toolbarItem} />
                </div>
              );
            })}
        </Stack>

        {navigationBarDisplayMode !== "large" && (
          <Stack
            sx={{
              overflow: "hidden",
              gap: "4px",
            }}
          >
            {titleTypography}

            {subtitleTypography}
          </Stack>
        )}

        <Stack
          direction="row"
          sx={{
            gap: "8px",
            maxWidth: "70px",
            minWidth: "70px",
            justifyContent: "end",
          }}
        >
          {navigationBarTrailing &&
            navigationBarTrailing.map((toolbarItem, index) => {
              return (
                <div key={index}>
                  {!!count && (
                    <Box
                      sx={{
                        position: "absolute",
                        display: "grid",
                        backgroundColor: ColoursObj.states.error,
                        borderRadius: "50%",
                        height: "16px",
                        width: "16px",
                        justifyItems: "center",
                        alignItems: "center",
                        translate: "25px -5px",
                      }}
                    >
                      <Typography
                        color={ColoursObj.shades.shade5}
                        variant="bodySubtext"
                        sx={{
                          fontSize: count > 9 ? "8px" : "12px",
                          lineHeight: count > 9 ? "8px" : "12px",
                        }}
                      >
                        {count}
                      </Typography>
                    </Box>
                  )}

                  <ToolbarItem
                    props={{
                      ...toolbarItem,
                      profilePage,
                    }}
                  />
                </div>
              );
            })}
        </Stack>
      </Stack>
      {stickyUnderlay && stickyUnderlay}

      <Box />
    </Stack>
  );

  // The content to display wrapped in a Page Transition.
  const withPageTransition = (
    <div>
      <PageTransition rootZIndex={60}>
        <div
          style={{
            boxShadow: pictureMode ? "" : !atTop ? "0px 4px 8px 0px rgba(51, 82, 111, 0.1)" : undefined,
            transition: "0.5s",
          }}
        >
          {navigationBar}
        </div>
      </PageTransition>

      {/* Clears the fixed web header above (PageTransition is position: fixed). Under iOS
          native navigation that header is null and the native bar sits outside the webview,
          so the offset would just be dead space under the title. */}
      <div className="content" style={{ paddingTop: userSearchBar && !isIosNativeNavigation() ? "105px" : "" }}>
        {body}
      </div>

      {/* background cover for the area below the footer */}
      {footer && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            width: "100%",
            height: footerBottom,
            backgroundColor: ColoursObj.backgrounds.primary,
            zIndex: 59, // beneath navbar with 60
          }}
        />
      )}

      <div
        id="NavigationViewFooter"
        style={{
          position: "fixed",
          bottom: footerBottom,
          width: "100%",
          backgroundColor: ColoursObj.backgrounds.primary,
          // MUI gives an outlined TextField label position: absolute; z-index: 1, which would otherwise
          // paint over this fixed footer (a positioned element with z-index auto).
          zIndex: 59, // beneath navbar with 60
        }}
        ref={footerRef}
      >
        {footer}
      </div>
    </div>
  );

  // The content to display with no Page Transition wrapped.
  const withoutPageTransition = (
    <>
      <Stack>
        <div
          style={{
            boxShadow: !atTop ? "0px 4px 8px 0px rgba(51, 82, 111, 0.1)" : undefined,
            transition: "0.5s",
            position: "fixed",
            width: "100%",
            zIndex: 600,
          }}
        >
          {navigationBar}
        </div>

        {body}

        {/* background cover for the area below the footer */}
        {footer && (
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              width: "100%",
              height: footerBottom,
              backgroundColor: ColoursObj.backgrounds.primary,
              zIndex: 59, // beneath navbar with 60
            }}
          />
        )}

        <div
          id="NavigationViewFooter"
          style={{
            position: "fixed",
            bottom: footerBottom,
            width: "100%",
            backgroundColor: ColoursObj.backgrounds.primary,
            // MUI gives an outlined TextField label position: absolute; z-index: 1, which would otherwise
            // paint over this fixed footer (a positioned element with z-index auto).
            zIndex: 59, // beneath navbar with 60
          }}
          ref={footerRef}
        >
          {footer}
        </div>
      </Stack>
    </>
  );

  // Returns the appropriate content dependent on the usePageTransition
  // property.
  const content =
    usePageTransition !== undefined
      ? usePageTransition
        ? withPageTransition
        : withoutPageTransition
      : withoutPageTransition;

  // The native bar's leading items: navigationBarLeading (which the web header also
  // renders) wins, else nativeLeadingButton, else nothing.
  const nativeLeading =
    navigationBarLeading && navigationBarLeading.length > 0
      ? navigationBarLeading
      : nativeLeadingButton
        ? [nativeLeadingButton]
        : undefined;

  return (
    <>
      {isIosNativeNavigation() && (
        <NativeToolbar
          leading={nativeLeading}
          trailing={navigationBarTrailing}
          onClose={backButtonAction}
          cancellable={cancellable}
          onDismissAttempt={onDismissAttempt}
          supplementBackButton={supplementBackButton}
          backMayRefuse={backMayRefuse}
        />
      )}
      {content}
    </>
  );
};

// A wrapper to apply page transition animations, designed to be used with
// Navigation View.
const PageTransition = ({ children, rootZIndex }: { children: React.ReactNode; rootZIndex: number }) => {
  return (
    <div
      className="header-animation"
      style={{
        zIndex: rootZIndex,
        width: "var(--app-width)",
        position: "fixed",
      }}
    >
      {children}
    </div>
  );
};
